<template>
    <v-dialog v-model="showDialog" persistent width="650">
      <v-card>
        <v-card-title class="text-h5">
          <span v-if="hasPraxisTags && hasAddressTags">Daten unvollständig</span>
          <span v-else-if="hasPraxisTags">Praxisdaten unvollständig</span>
          <span v-else>Adressdaten unvollständig</span>
        </v-card-title>
        <v-card-text class="my-5 text-body-1">
          <p v-if="isQuickInvoice">
            <strong>Die Rechnung wurde erstellt, es fehlen jedoch folgende Angaben um die Rechnung per E-Mail versenden zu können:</strong>
          </p>
          <template v-else>
            <p v-if="hasPraxisTags && hasAddressTags">Es fehlen noch folgende Angaben für eine vollständige Rechnung:</p>
            <p v-else-if="hasPraxisTags">Es fehlen noch folgende Angaben zu deiner Praxis für eine vollständige Rechnung:</p>
            <p v-else>Es fehlen noch folgende Adressangaben für eine vollständige Rechnung:</p>
          </template>
          
          <div class="mt-3">
            <template v-if="hasPraxisTags && hasAddressTags">
              <p class="font-weight-medium">Praxisdaten:</p>
              <ul>
                <li v-for="(tag, index) in praxisTags" :key="'praxis-'+index">
                  {{ lookupMissingData[tag] }}
                </li>
              </ul>
              
              <p class="font-weight-medium mt-4">Adressdaten:</p>
              <ul>
                <li v-for="(tag, index) in addressTags" :key="'address-'+index">
                  {{ lookupMissingData[tag] }}
                </li>
              </ul>
            </template>
            <template v-else>
              <ul>
                <li v-for="(tag, index) in displayedTags" :key="index">
                  {{ lookupMissingData[tag] }}
                </li>
              </ul>
            </template>
          </div>
        </v-card-text>
  
        <v-card-actions class="px-6 pb-5 flex-wrap">
          <v-btn 
            class="ma-1"
            :color="$store.state.theme.red"
            :block="$vuetify.breakpoint.smAndDown"
            text 
            @click="cancel"
          >
            {{ isQuickInvoice ? 'E-Mail Versand Überspringen' : 'Abbrechen' }}
          </v-btn>
          <v-spacer/>
          <v-btn 
            v-if="!isQuickInvoice && showProceedButton" 
            class="ma-1"
            :color="$store.state.theme.primary" 
            :block="$vuetify.breakpoint.smAndDown"
            outlined 
            @click="proceed"
          >
            Trotzdem erstellen
          </v-btn>
          <v-btn 
            class="ma-1"
            :color="$store.state.theme.green" 
            :block="$vuetify.breakpoint.smAndDown"
            outlined 
            @click="navigate"
          >
            Daten vervollständigen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  export default {
    name: 'MissingDataDialog',
    
    props: {
      // Controls the dialog visibility
      value: {
        type: Boolean,
        default: false
      },
      
      // List of missing tags
      missingTags: {
        type: Array,
        default: () => []
      },
      
      // Show the "Proceed Anyway" button
      showProceedButton: {
        type: Boolean,
        default: false
      },
      
      // Is this being used in the Quick Invoice flow
      isQuickInvoice: {
        type: Boolean,
        default: false
      },
      
      // Client ID to use in navigation (if relevant)
      clientId: {
        type: [Number, String],
        default: null
      }
    },
    
    data() {
      return {
        // Lookup for display of missing fields
        lookupMissingData: {
          'adresse': 'Adresse des Klienten',
          'plz': 'Postleitzahl des Klienten',
          'ort': 'Ort des Klienten',
          'vater_adresse': 'Adresse des Vaters',
          'vater_plz': 'Postleitzahl des Vaters',
          'vater_ort': 'Ort des Vaters',
          'mutter_adresse': 'Adresse der Mutter',
          'mutter_plz': 'Postleitzahl der Mutter',
          'mutter_ort': 'Ort der Mutter',
          'partner_adresse': 'Adresse des Partners',
          'partner_plz': 'Postleitzahl des Partners',
          'partner_ort': 'Ort des Partners',

          'praxis_name': 'Name deiner Praxis',
          'praxis_telefon': 'Telefonnummer deiner Praxis',
          'praxis_adresse': 'Adresse deiner Praxis',
          'praxis_plz': 'Postleitzahl deiner Praxis',
          'praxis_ort': 'Ort deiner Praxis',
          'praxis_iban': 'IBAN des Praxiskontos',
          'praxis_bic': 'BIC des Praxiskontos',
          'bezeichnung': 'Berufsbezeichnung',
        },
      };
    },
    
    computed: {
      // Used to control the dialog visibility via v-model
      showDialog: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        }
      },
      
      // Determines whether we're working with client data or praxis data
      navigateToClients() {
        // Check if there are missing praxis tags
        const hasPraxisTags = this.missingTags.some(tag => tag.startsWith('praxis_') || tag === 'bezeichnung');
        
        // If there are praxis tags missing, prioritize those
        if (hasPraxisTags) {
          return false;
        }
        
        // Otherwise, show client tags if there are any
        return this.missingTags.some(tag => !tag.startsWith('praxis_') && tag !== 'bezeichnung');
      },
      
      // The tags to display in the dialog
      displayedTags() {
        // Check if there are missing praxis tags
        const hasPraxisTags = this.missingTags.some(tag => tag.startsWith('praxis_') || tag === 'bezeichnung');
        
        // If there are praxis tags missing, only show those
        if (hasPraxisTags) {
          return this.missingTags.filter(tag => tag.startsWith('praxis_') || tag === 'bezeichnung');
        }
        
        // Otherwise, show client tags
        return this.missingTags.filter(tag => !tag.startsWith('praxis_') && tag !== 'bezeichnung');
      },
      
      // Gets missing praxis tags
      praxisTags() {
        return this.missingTags.filter(tag => tag.startsWith('praxis_') || tag === 'bezeichnung');
      },
      
      // Gets missing address tags
      addressTags() {
        return this.missingTags.filter(tag => !tag.startsWith('praxis_') && tag !== 'bezeichnung');
      },
      
      // Determines whether we're working with praxis data
      hasPraxisTags() {
        return this.missingTags.some(tag => tag.startsWith('praxis_') || tag === 'bezeichnung');
      },
      
      // Determines whether we're working with address data
      hasAddressTags() {
        return this.missingTags.some(tag => !tag.startsWith('praxis_') && tag !== 'bezeichnung');
      }
    },
    
    methods: {
      // Cancel button was clicked
      cancel() {
        this.showDialog = false;
        this.$emit('cancel');
      },
      
      // "Proceed Anyway" button was clicked
      proceed() {
        this.showDialog = false;
        this.$emit('proceed');
      },
      
      // "Navigate to" button was clicked
      navigate() {
        this.showDialog = false;
        
        // If both types of tags are missing, prioritize praxis settings
        // But include all relevant tags for the target page
        const navigateToClients = !this.hasPraxisTags && this.hasAddressTags;
        
        // If we're going to settings, include only praxis tags
        // If we're going to client page, include only address tags
        const tagsToInclude = navigateToClients ? this.addressTags : this.praxisTags;
        
        this.$emit('navigate', {
          navigateToClients: navigateToClients,
          clientId: this.clientId,
          missingTags: tagsToInclude
        });
      }
    }
  };
  </script>